import { useEffect, useMemo, useState } from 'react';
import { ROUND_WARNING_SECONDS } from 'constants/play';
import type { RoundState } from 'types/interfaces/play';
import { formatClock } from '../playFormat';

export type ClockTone = 'normal' | 'warning' | 'overtime';

export interface RoundClock {
  /** Seconds left; negative while in overtime after Time called */
  remainingSeconds: number;
  /** MM:SS, with a leading + in overtime */
  text: string;
  tone: ClockTone;
  isRunning: boolean;
  isPaused: boolean;
  /** Round not started yet: shows the full duration */
  notStarted: boolean;
}

type Anchor = { base: number; at: number; running: boolean };

export const anchorFor = (round: Pick<RoundState, 'remainingSeconds' | 'startedAt' | 'pausedAt' | 'durationSeconds'> | null | undefined, now: number): Anchor => {
  if (!round) return { base: 0, at: now, running: false };
  if (!round.startedAt) return { base: round.durationSeconds, at: now, running: false };
  return { base: round.remainingSeconds, at: now, running: round.pausedAt === null };
};

export const remainingAt = (anchor: Anchor, now: number): number =>
  anchor.running ? Math.round(anchor.base - (now - anchor.at) / 1000) : Math.round(anchor.base);

export const toneFor = (remaining: number): ClockTone => (remaining <= 0 ? 'overtime' : remaining <= ROUND_WARNING_SECONDS ? 'warning' : 'normal');

/**
 * Decision 12: the server is authoritative. Every RoundState carries
 * remainingSeconds computed at serverTime, so the client anchors on it and
 * subtracts local elapsed time only while the round is running. Pause,
 * resume, extend and start each deliver a new RoundState and re-anchor. The
 * clock never ends a round: at 0 it turns red and counts up.
 */
export const useRoundClock = (round: RoundState | null | undefined): RoundClock => {
  const [anchor, setAnchor] = useState<Anchor>(() => anchorFor(round, Date.now()));
  const [now, setNow] = useState(() => Date.now());

  const key = round ? `${round.id}|${round.startedAt}|${round.pausedAt}|${round.pausedTotalSeconds}|${round.durationSeconds}|${round.remainingSeconds}|${round.serverTime}` : 'none';
  useEffect(() => {
    const at = Date.now();
    setAnchor(anchorFor(round, at));
    setNow(at);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => {
    if (!anchor.running) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    const onVisible = () => setNow(Date.now());
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [anchor]);

  return useMemo(() => {
    const remaining = remainingAt(anchor, now);
    return {
      remainingSeconds: remaining,
      text: formatClock(remaining),
      tone: toneFor(remaining),
      isRunning: anchor.running,
      isPaused: !!round?.startedAt && round.pausedAt !== null,
      notStarted: !round?.startedAt,
    };
  }, [anchor, now, round?.startedAt, round?.pausedAt]);
};

export default useRoundClock;
